/**
 * Backup/Restore Manager
 * Settings page section for exporting/importing application data
 * using zip-based backup format (one JSON file per table + metadata.json).
 *
 * Requirements: 15.1, 15.2, 15.3, 15.4, 15.5
 */

import JSZip from 'jszip';
import { exportAllData, importData } from '../../services/api.js';

// ── Constants ───────────────────────────────────────────────────────────────

const BACKUP_TABLES = [
  'aircraft',
  'pilots',
  'flights',
  'flight_fuel_oil',
  'defects',
  'maintenance_events',
  'billing_rates',
  'invoices',
  'airports',
  'aircraft_airports',
  'pilot_licences',
  'licence_aircraft_mappings',
  'currency_thresholds',
];

/** Independent tables that must be imported first (no foreign key deps). */
const INDEPENDENT_TABLES = ['aircraft', 'pilots', 'airports'];

/** Dependent tables imported after independent ones. */
const DEPENDENT_TABLES = BACKUP_TABLES.filter((t) => !INDEPENDENT_TABLES.includes(t));

// ── Pure helper functions (exported for testing) ────────────────────────────

/**
 * Returns the ordered list of tables for import.
 * Independent tables first, then dependent tables.
 * @returns {string[]}
 */
export function getImportOrder() {
  return [...INDEPENDENT_TABLES, ...DEPENDENT_TABLES];
}

/**
 * Build export metadata from table data.
 * @param {Object} data - Object keyed by table name with arrays of records
 * @returns {{ export_date: string, app_version: string, record_counts: Object }}
 */
export function buildExportMetadata(data) {
  const record_counts = {};
  for (const table of BACKUP_TABLES) {
    record_counts[table] = Array.isArray(data[table]) ? data[table].length : 0;
  }
  return {
    export_date: new Date().toISOString(),
    app_version: '1.2',
    record_counts,
  };
}

/**
 * Validate the structure of a zip archive by checking its file list.
 * Returns whether metadata.json is present and which expected table files are missing.
 * @param {string[]} fileList - Array of filenames from the zip archive
 * @returns {{ valid: boolean, missingTables: string[], hasMetadata: boolean }}
 */
export function validateZipStructure(fileList) {
  const hasMetadata = fileList.includes('metadata.json');
  const missingTables = BACKUP_TABLES.filter(
    (table) => !fileList.includes(`${table}.json`),
  );
  const valid = hasMetadata && missingTables.length === 0;
  return { valid, missingTables, hasMetadata };
}

/**
 * Format a date string for the backup filename.
 * @param {Date} [date]
 * @returns {string} YYYY-MM-DD
 */
function formatDateForFilename(date = new Date()) {
  return date.toISOString().split('T')[0];
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * createBackupManager() → HTMLElement
 *
 * Returns a section element containing Export and Restore controls.
 */
export function createBackupManager() {
  const section = document.createElement('section');
  section.className = 'settings-section backup-manager';

  const heading = document.createElement('h2');
  heading.textContent = 'Backup & Restore';
  section.appendChild(heading);

  // Message area
  const messageArea = document.createElement('div');
  messageArea.className = 'backup-manager__message';
  section.appendChild(messageArea);

  // Progress area
  const progressArea = document.createElement('div');
  progressArea.className = 'backup-manager__progress';
  progressArea.style.display = 'none';
  section.appendChild(progressArea);

  // Summary area (for restore confirmation)
  const summaryArea = document.createElement('div');
  summaryArea.className = 'backup-manager__summary';
  summaryArea.style.display = 'none';
  section.appendChild(summaryArea);

  // Buttons container
  const btnContainer = document.createElement('div');
  btnContainer.className = 'backup-manager__buttons';

  const exportBtn = document.createElement('button');
  exportBtn.type = 'button';
  exportBtn.className = 'btn btn--primary';
  exportBtn.textContent = 'Export Data';

  const restoreBtn = document.createElement('button');
  restoreBtn.type = 'button';
  restoreBtn.className = 'btn btn--secondary';
  restoreBtn.textContent = 'Restore Data';

  // Hidden file input for restore
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.zip';
  fileInput.style.display = 'none';

  btnContainer.appendChild(exportBtn);
  btnContainer.appendChild(restoreBtn);
  btnContainer.appendChild(fileInput);
  section.appendChild(btnContainer);

  // ── Helpers ─────────────────────────────────────────────────────────────

  function showMessage(text, type = 'success') {
    messageArea.textContent = text;
    messageArea.className = `backup-manager__message backup-manager__message--${type}`;
    setTimeout(() => {
      messageArea.textContent = '';
      messageArea.className = 'backup-manager__message';
    }, 6000);
  }

  function showProgress(text) {
    progressArea.textContent = text;
    progressArea.style.display = 'block';
  }

  function hideProgress() {
    progressArea.textContent = '';
    progressArea.style.display = 'none';
  }

  function setButtonsDisabled(disabled) {
    exportBtn.disabled = disabled;
    restoreBtn.disabled = disabled;
  }

  // ── Export ──────────────────────────────────────────────────────────────

  exportBtn.addEventListener('click', async () => {
    setButtonsDisabled(true);
    showProgress('Exporting data…');
    summaryArea.style.display = 'none';

    try {
      const backup = await exportAllData();
      const metadata = backup.metadata;
      const data = backup.data;

      // Build zip archive
      const zip = new JSZip();
      zip.file('metadata.json', JSON.stringify(metadata, null, 2));
      for (const table of BACKUP_TABLES) {
        const tableData = data[table] || [];
        zip.file(`${table}.json`, JSON.stringify(tableData, null, 2));
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const filename = `alliavia-backup-${formatDateForFilename()}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showMessage(`Backup exported as ${filename}`);
    } catch (err) {
      showMessage(err.message || 'Export failed', 'error');
    } finally {
      hideProgress();
      setButtonsDisabled(false);
    }
  });

  // ── Restore ─────────────────────────────────────────────────────────────

  restoreBtn.addEventListener('click', () => {
    fileInput.value = '';
    fileInput.click();
  });

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files[0];
    if (!file) return;

    let zip;
    try {
      const arrayBuffer = await file.arrayBuffer();
      zip = await JSZip.loadAsync(arrayBuffer);
    } catch (_) {
      showMessage('Invalid backup file — expected a zip archive', 'error');
      return;
    }

    // Get file list from zip
    const fileList = Object.keys(zip.files);
    const validation = validateZipStructure(fileList);

    // metadata.json is required
    if (!validation.hasMetadata) {
      showMessage('Invalid backup archive — missing metadata', 'error');
      return;
    }

    // Parse metadata
    let metadata;
    try {
      const metadataText = await zip.file('metadata.json').async('string');
      metadata = JSON.parse(metadataText);
    } catch (_) {
      showMessage('Invalid backup archive — corrupt metadata', 'error');
      return;
    }

    // Parse table data from zip
    const data = {};
    for (const table of BACKUP_TABLES) {
      const zipFile = zip.file(`${table}.json`);
      if (zipFile) {
        try {
          const text = await zipFile.async('string');
          data[table] = JSON.parse(text);
        } catch (_) {
          showMessage(`Invalid backup archive — corrupt data for table "${table}"`, 'error');
          return;
        }
      }
    }

    // If some tables are missing, show warning and allow partial restore
    if (validation.missingTables.length > 0) {
      showPartialRestoreWarning(metadata, data, validation.missingTables);
    } else {
      showRestoreSummary(metadata, data);
    }
  });

  function showPartialRestoreWarning(metadata, data, missingTables) {
    summaryArea.innerHTML = '';
    summaryArea.style.display = 'block';

    const title = document.createElement('h3');
    title.textContent = 'Incomplete Backup';
    summaryArea.appendChild(title);

    const warning = document.createElement('p');
    warning.className = 'backup-manager__warning';
    warning.textContent = `Warning: The following tables are missing from the backup: ${missingTables.join(', ')}`;
    summaryArea.appendChild(warning);

    const info = document.createElement('p');
    info.textContent = `Export date: ${metadata.export_date}`;
    summaryArea.appendChild(info);

    const presentTables = BACKUP_TABLES.filter((t) => !missingTables.includes(t));
    const countsList = document.createElement('ul');
    for (const table of presentTables) {
      const count = Array.isArray(data[table]) ? data[table].length : 0;
      const li = document.createElement('li');
      li.textContent = `${table}: ${count} records`;
      countsList.appendChild(li);
    }
    summaryArea.appendChild(countsList);

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'btn btn--danger';
    confirmBtn.textContent = 'Restore Available Tables';
    confirmBtn.addEventListener('click', () => performRestore(data));
    summaryArea.appendChild(confirmBtn);

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn--secondary';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      summaryArea.style.display = 'none';
      summaryArea.innerHTML = '';
    });
    summaryArea.appendChild(cancelBtn);
  }

  function showRestoreSummary(metadata, data) {
    summaryArea.innerHTML = '';
    summaryArea.style.display = 'block';

    const title = document.createElement('h3');
    title.textContent = 'Restore Summary';
    summaryArea.appendChild(title);

    const info = document.createElement('p');
    info.textContent = `Export date: ${metadata.export_date}`;
    summaryArea.appendChild(info);

    const countsList = document.createElement('ul');
    for (const table of BACKUP_TABLES) {
      const count = metadata.record_counts?.[table] || 0;
      const li = document.createElement('li');
      li.textContent = `${table}: ${count} records`;
      countsList.appendChild(li);
    }
    summaryArea.appendChild(countsList);

    const warning = document.createElement('p');
    warning.className = 'backup-manager__warning';
    warning.textContent = 'Warning: Existing data may be overwritten. This action cannot be undone.';
    summaryArea.appendChild(warning);

    const confirmBtn = document.createElement('button');
    confirmBtn.type = 'button';
    confirmBtn.className = 'btn btn--danger';
    confirmBtn.textContent = 'Confirm Restore';
    confirmBtn.addEventListener('click', () => performRestore(data));
    summaryArea.appendChild(confirmBtn);

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn--secondary';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', () => {
      summaryArea.style.display = 'none';
      summaryArea.innerHTML = '';
    });
    summaryArea.appendChild(cancelBtn);
  }

  async function performRestore(data) {
    summaryArea.style.display = 'none';
    summaryArea.innerHTML = '';
    setButtonsDisabled(true);

    const importOrder = getImportOrder();
    const results = {};

    try {
      for (const table of importOrder) {
        const records = data[table];
        if (!records || records.length === 0) {
          results[table] = 0;
          continue;
        }
        showProgress(`Importing ${table}…`);
        try {
          const imported = await importData(records, table);
          results[table] = Array.isArray(imported) ? imported.length : records.length;
        } catch (tableErr) {
          // Partial failure — report which table failed
          hideProgress();
          setButtonsDisabled(false);
          const importedSoFar = Object.entries(results)
            .map(([t, c]) => `${t}: ${c}`)
            .join(', ');
          showMessage(
            `Restore failed on table "${table}". Records imported before failure: ${importedSoFar || 'none'}`,
            'error',
          );
          return;
        }
      }

      hideProgress();
      setButtonsDisabled(false);

      // Success message with per-table counts
      const countSummary = Object.entries(results)
        .map(([t, c]) => `${t}: ${c}`)
        .join(', ');
      showMessage(`Restore complete. Records imported — ${countSummary}`);
    } catch (err) {
      hideProgress();
      setButtonsDisabled(false);
      showMessage(err.message || 'Restore failed', 'error');
    }
  }

  return section;
}
